import { StateManager } from '../../managers/StateManager';
import { WirePreview } from '../../entities/WirePreview';
import { PinHit } from '../../entities/Connector';
import { InteractionState, Connector as ConnectorData } from '../../types';
import { CONNECTOR } from '../../utils/constants';
import { connectorCenter } from '../../utils/geometry';
import { connectorHeight } from '../../utils/connectorLayout';
import { logger } from '../../utils/logger';
import { ContextMenuHandlers, PixiAppRefs } from './types';

/** Click tolerance (world units) for hitting an existing node during wire creation. */
const NODE_CLICK_TOLERANCE = 12;

/** Gauge (mm²) a freshly drawn wire starts at when there is no previous wire to copy. */
const DEFAULT_NEW_WIRE_GAUGE = 0.35;

/** A wire-creation preview, guaranteed present once the handler has validated it. */
type PreviewWire = NonNullable<InteractionState['previewWire']>;

/** Resolves the pin gutter row (if any) under a world point on the given connector. */
function resolvePinHit(refs: PixiAppRefs, connector: ConnectorData, worldX: number, worldY: number): PinHit | null {
  const entity = refs.connectorEntities.current.get(connector.id);
  if (!entity) {
    return null;
  }
  return entity.pinAt(worldX - connector.xPosition, worldY - connector.yPosition);
}

/**
 * Finishes a wire on the resolved target pin, adding a pin on the fly when the
 * "add pin" row was hit. The pin add and the wire are one undo step, then wire
 * creation returns to normal and the routed nodes become permanent. The new wire
 * inherits the previous wire's colour and gauge (defaulting the gauge for the
 * very first wire) and its properties dialog opens so the user can adjust it.
 */
function completeWire(
  stateManager: StateManager,
  refs: PixiAppRefs,
  contextMenuHandlers: ContextMenuHandlers,
  previewWire: PreviewWire,
  endConnectorId: string,
  hit: PinHit
): void {
  stateManager.beginBatch();
  const endPinId = hit.kind === 'pin' ? hit.pinId : stateManager.addPin(endConnectorId);
  const wires = stateManager.getState().wires;
  const previous = wires[wires.length - 1];
  const wireId = stateManager.generateUniqueId();
  stateManager.addWire({
    id: wireId,
    name: stateManager.generateWireName(),
    note: '',
    startConnectorId: previewWire.startConnectorId,
    startPinId: previewWire.startPinId,
    endConnectorId,
    endPinId,
    path: [...previewWire.pathNodes],
    color: previous?.color,
    gauge: previous?.gauge ?? DEFAULT_NEW_WIRE_GAUGE
  });
  stateManager.endBatch();

  refs.temporaryNodeIds.current = [];
  stateManager.setInteractionMode('normal');
  stateManager.setPreviewWire(undefined);

  contextMenuHandlers.handleWireEdit(wireId);
}

/**
 * Builds the click/cancel handlers that drive wire creation: adding path nodes,
 * completing a wire on a second connector, and discarding temporary nodes.
 */
export function createWireCreationHandlers(refs: PixiAppRefs, stateManager: StateManager, contextMenuHandlers: ContextMenuHandlers) {
  const handleWireCreationClick = (worldX: number, worldY: number): void => {
    const interactionState = stateManager.getInteractionState();
    if (interactionState.mode !== 'wireCreation' || !interactionState.previewWire) {
      return;
    }

    // Check if clicking on a connector
    const wires = stateManager.getState().wires;
    const clickedConnector = stateManager.getState().connectors.find(connector => {
      const dx = worldX - connector.xPosition;
      const dy = worldY - connector.yPosition;
      return dx >= 0 && dx <= CONNECTOR.WIDTH && dy >= 0 && dy <= connectorHeight(connector, wires);
    });

    if (clickedConnector) {
      // The wire must terminate on a pin: complete only when a gutter row is hit,
      // otherwise wait for the user to click a pin on the connector.
      const hit = resolvePinHit(refs, clickedConnector, worldX, worldY);
      if (!hit) {
        return;
      }
      // Terminating on the same pin the wire started from would be a zero jumper.
      const sameConnector = clickedConnector.id === interactionState.previewWire.startConnectorId;
      if (sameConnector && hit.kind === 'pin' && hit.pinId === interactionState.previewWire.startPinId) {
        return;
      }
      completeWire(stateManager, refs, contextMenuHandlers, interactionState.previewWire, clickedConnector.id, hit);
      return;
    }

    // Check if clicking on an existing node
    const clickedNode = stateManager.getState().nodes.find(node => {
      const dx = worldX - node.xPosition;
      const dy = worldY - node.yPosition;
      return Math.sqrt(dx * dx + dy * dy) <= NODE_CLICK_TOLERANCE;
    });

    if (clickedNode) {
      stateManager.setPreviewWire({
        ...interactionState.previewWire,
        pathNodes: [...interactionState.previewWire.pathNodes, clickedNode.id],
        currentX: worldX,
        currentY: worldY
      });

      if (refs.wirePreview.current) {
        refs.wirePreview.current.addNode(clickedNode.xPosition, clickedNode.yPosition);
      }
      return;
    }

    // Create a new node at the click position
    const newNode = {
      id: stateManager.generateUniqueId(),
      xPosition: worldX,
      yPosition: worldY
    };

    stateManager.addNode(newNode);
    refs.temporaryNodeIds.current.push(newNode.id);

    stateManager.setPreviewWire({
      ...interactionState.previewWire,
      pathNodes: [...interactionState.previewWire.pathNodes, newNode.id],
      currentX: worldX,
      currentY: worldY
    });

    if (refs.wirePreview.current) {
      refs.wirePreview.current.addNode(worldX, worldY);
    }
  };

  const handleWireCreationCancel = (): void => {
    // Remove any temporary nodes created during this wire creation session
    if (refs.temporaryNodeIds.current.length > 0) {
      refs.temporaryNodeIds.current.forEach(nodeId => {
        stateManager.removeNode(nodeId);
      });
      refs.temporaryNodeIds.current = [];
    }

    stateManager.setInteractionMode('normal');
    stateManager.setPreviewWire(undefined);
  };

  return { handleWireCreationClick, handleWireCreationCancel };
}

/**
 * Builds the handler that reacts to interaction-state changes, creating or
 * tearing down the wire preview as wire-creation mode is entered or left.
 */
export function createInteractionStateHandler(refs: PixiAppRefs, stateManager: StateManager) {
  return (interactionState: InteractionState): void => {
    if (!refs.mounted.current || !refs.viewportContainer.current || !refs.interactionManager.current) {
      return;
    }

    logger.interaction.debug('Interaction state changed', interactionState);

    refs.interactionManager.current.setInteractionState(interactionState);
    const viewport = refs.viewportContainer.current;

    if (interactionState.mode === 'wireCreation' && interactionState.previewWire) {
      if (!refs.wirePreview.current) {
        const startConnector = stateManager.getState().connectors.find(t => t.id === interactionState.previewWire!.startConnectorId);
        if (startConnector) {
          const center = connectorCenter(startConnector);
          refs.wirePreview.current = new WirePreview(center.x, center.y);
          viewport.addChild(refs.wirePreview.current);
        }
      }
      return;
    }

    if (refs.wirePreview.current) {
      viewport.removeChild(refs.wirePreview.current);
      refs.wirePreview.current = null;
    }
  };
}
